import {ADataGridColumn, ADataGridFilter} from "@atiautomacao/ati-ui-library";
import {format, isValid, parse, parseISO} from "date-fns";

type DateFilterOutputFormat = "local-date-time" | "instant";

export interface DateFilterDefinition {
    label: string;
    outputFormat?: DateFilterOutputFormat;
}

export type DateFilterDefinitions = Record<string, DateFilterDefinition>;

export const ALARM_DATE_FILTER_NAMES = ["dateTime", "clearDate", "acknowledgementDate", "dateTimeNote"];

export const DATE_FILTER_DEFINITIONS: DateFilterDefinitions = {
    dateTime: {label: "Início"},
    clearDate: {label: "Finalizado"},
    acknowledgementDate: {label: "Reconhecido"},
    dateTimeNote: {label: "Data da nota"},
    dateTimeReq: {label: "Envio"},
    dateTimeRes: {label: "Recebido"},
    timeStamp: {label: "Data e Hora", outputFormat: "instant"},
    startDateTime: {label: "Início"},
    endDateTime: {label: "Término"},
};

export const ALARM_DATE_FILTER_DEFINITIONS: DateFilterDefinitions = ALARM_DATE_FILTER_NAMES.reduce(
    (definitions, filterName) => ({
        ...definitions,
        [filterName]: DATE_FILTER_DEFINITIONS[filterName],
    }),
    {} as DateFilterDefinitions
);

const DATE_INPUT_ALLOWED_CHARS = /[^0-9/:\-\s]/g;
const ISO_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
const ISO_DATE_TIME_WITH_TIMEZONE_PATTERN =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:?\d{2})$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

const USER_DATE_TIME_FORMATS = [
    "dd/MM/yyyy HH:mm:ss",
    "dd-MM-yyyy HH:mm:ss",
    "dd/MM/yyyy HH:mm",
    "dd-MM-yyyy HH:mm",
    "dd/MM/yyyy",
    "dd-MM-yyyy",
];

export interface AlarmDateFilterSanitizationResult {
    filters: Array<ADataGridFilter>;
    sanitizedFieldLabels: Array<string>;
}

export interface AlarmDateFilterValidationResult {
    filters: Array<ADataGridFilter>;
    invalidFieldLabels: Array<string>;
    isValid: boolean;
}

export interface DateFilterProcessingResult {
    /** Filtros sanitizados (valores de exibição). */
    filters: Array<ADataGridFilter>;
    /** Filtros convertidos para ISO quando válidos; senão iguais a `filters`. */
    convertedFilters: Array<ADataGridFilter>;
    sanitizedFieldLabels: Array<string>;
    invalidFieldLabels: Array<string>;
    wasSanitized: boolean;
    isValid: boolean;
}

export type DateFilterSanitizationResult = AlarmDateFilterSanitizationResult;
export type DateFilterValidationResult = AlarmDateFilterValidationResult;
export type AlarmDateFilterProcessingResult = DateFilterProcessingResult;

const uniqueLabels = (labels: Array<string>): Array<string> =>
    labels.filter((label, index) => labels.indexOf(label) === index);

const resolveDateFilterDefinition = (
    filterName: string,
    definitions: DateFilterDefinitions = DATE_FILTER_DEFINITIONS
): DateFilterDefinition | undefined => definitions[filterName];

export const isDateFilter = (
    filter: ADataGridFilter,
    definitions: DateFilterDefinitions = DATE_FILTER_DEFINITIONS
): boolean => Boolean(resolveDateFilterDefinition(filter.name, definitions));

export const isAlarmDateFilter = (filter: ADataGridFilter): boolean =>
    isDateFilter(filter, ALARM_DATE_FILTER_DEFINITIONS);

export const getDateFilterLabel = (
    filterName: string,
    definitions: DateFilterDefinitions = DATE_FILTER_DEFINITIONS
): string => resolveDateFilterDefinition(filterName, definitions)?.label || filterName;

export const getAlarmDateFilterLabel = (filterName: string): string =>
    getDateFilterLabel(filterName, ALARM_DATE_FILTER_DEFINITIONS);

export const sanitizeDateFilterValue = (value: string): string =>
    value.replace(DATE_INPUT_ALLOWED_CHARS, "");

export const sanitizeAlarmDateFilterValue = sanitizeDateFilterValue;

export const sanitizeDateFilters = (
    filters: Array<ADataGridFilter>,
    definitions: DateFilterDefinitions = DATE_FILTER_DEFINITIONS
): DateFilterSanitizationResult => {
    const sanitizedFieldLabels: Array<string> = [];

    const sanitizedFilters = filters.map((filter) => {
        if (!isDateFilter(filter, definitions)) {
            return {...filter};
        }

        const originalValue = filter.value || "";
        const sanitizedValue = sanitizeDateFilterValue(originalValue);

        if (sanitizedValue !== originalValue) {
            sanitizedFieldLabels.push(getDateFilterLabel(filter.name, definitions));
        }

        return {
            ...filter,
            value: sanitizedValue,
        };
    });

    return {
        filters: sanitizedFilters,
        sanitizedFieldLabels: uniqueLabels(sanitizedFieldLabels),
    };
};

export const sanitizeAlarmDateFilters = (filters: Array<ADataGridFilter>): AlarmDateFilterSanitizationResult =>
    sanitizeDateFilters(filters, ALARM_DATE_FILTER_DEFINITIONS);

const tryParseStrictDate = (value: string, dateFormat: string): Date | null => {
    const parsedDate = parse(value, dateFormat, new Date());

    if (!isValid(parsedDate) || format(parsedDate, dateFormat) !== value) {
        return null;
    }

    return parsedDate;
};

const tryParseUserDateTime = (value: string): Date | null => {
    for (const dateFormat of USER_DATE_TIME_FORMATS) {
        const parsedDate = tryParseStrictDate(value, dateFormat);

        if (parsedDate) {
            return parsedDate;
        }
    }

    return null;
};

const formatAsInstant = (date: Date): string =>
    new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    )).toISOString();

export const convertDateFilterValueToISO = (
    value: string,
    definition: DateFilterDefinition = {label: ""}
): string | null => {
    const trimmedValue = value.trim();
    const outputFormat = definition.outputFormat || "local-date-time";

    if (trimmedValue.length === 0) {
        return "";
    }

    if (ISO_DATE_TIME_PATTERN.test(trimmedValue)) {
        const parsedISODate = parseISO(trimmedValue);

        if (isValid(parsedISODate) && format(parsedISODate, ISO_DATE_TIME_FORMAT) === trimmedValue) {
            return outputFormat === "instant"
                ? formatAsInstant(parsedISODate)
                : format(parsedISODate, ISO_DATE_TIME_FORMAT);
        }
    }

    if (ISO_DATE_TIME_WITH_TIMEZONE_PATTERN.test(trimmedValue)) {
        const parsedInstantDate = parseISO(trimmedValue);

        if (isValid(parsedInstantDate)) {
            return outputFormat === "instant"
                ? parsedInstantDate.toISOString()
                : format(parsedInstantDate, ISO_DATE_TIME_FORMAT);
        }
    }

    if (ISO_DATE_PATTERN.test(trimmedValue)) {
        const parsedISODate = tryParseStrictDate(trimmedValue, "yyyy-MM-dd");

        if (parsedISODate) {
            return outputFormat === "instant"
                ? formatAsInstant(parsedISODate)
                : format(parsedISODate, ISO_DATE_TIME_FORMAT);
        }
    }

    const parsedUserDate = tryParseUserDateTime(trimmedValue);

    if (parsedUserDate) {
        return outputFormat === "instant"
            ? formatAsInstant(parsedUserDate)
            : format(parsedUserDate, ISO_DATE_TIME_FORMAT);
    }

    return null;
};

export const convertAlarmDateFilterValueToISO = (value: string): string | null =>
    convertDateFilterValueToISO(value);

export const validateAndConvertDateFilters = (
    filters: Array<ADataGridFilter>,
    definitions: DateFilterDefinitions = DATE_FILTER_DEFINITIONS
): DateFilterValidationResult => {
    const invalidFieldLabels: Array<string> = [];

    const convertedFilters = filters.map((filter) => {
        const definition = resolveDateFilterDefinition(filter.name, definitions);

        if (!definition) {
            return {...filter};
        }

        const convertedValue = convertDateFilterValueToISO(filter.value || "", definition);

        if (convertedValue === null) {
            invalidFieldLabels.push(getDateFilterLabel(filter.name, definitions));
            return {...filter};
        }

        return {
            ...filter,
            value: convertedValue,
        };
    });

    const uniqueInvalidFieldLabels = uniqueLabels(invalidFieldLabels);

    return {
        filters: convertedFilters,
        invalidFieldLabels: uniqueInvalidFieldLabels,
        isValid: uniqueInvalidFieldLabels.length === 0,
    };
};

export const validateAndConvertAlarmDateFilters = (
    filters: Array<ADataGridFilter>
): AlarmDateFilterValidationResult => validateAndConvertDateFilters(filters, ALARM_DATE_FILTER_DEFINITIONS);

/**
 * Unifica sanitização + validação/conversão.
 * Se a sanitização alterar valores, retorna cedo (wasSanitized) sem validar —
 * o mesmo comportamento do early-return das telas de Alarmes.
 */
export const processDateFilters = (
    filters: Array<ADataGridFilter>,
    definitions: DateFilterDefinitions = DATE_FILTER_DEFINITIONS
): DateFilterProcessingResult => {
    const sanitizedResult = sanitizeDateFilters(filters, definitions);
    const wasSanitized = sanitizedResult.sanitizedFieldLabels.length > 0;

    if (wasSanitized) {
        return {
            filters: sanitizedResult.filters,
            convertedFilters: sanitizedResult.filters,
            sanitizedFieldLabels: sanitizedResult.sanitizedFieldLabels,
            invalidFieldLabels: [],
            wasSanitized: true,
            isValid: false,
        };
    }

    const validationResult = validateAndConvertDateFilters(sanitizedResult.filters, definitions);

    return {
        filters: sanitizedResult.filters,
        convertedFilters: validationResult.filters,
        sanitizedFieldLabels: [],
        invalidFieldLabels: validationResult.invalidFieldLabels,
        wasSanitized: false,
        isValid: validationResult.isValid,
    };
};

export const processAlarmDateFilters = (
    filters: Array<ADataGridFilter>
): AlarmDateFilterProcessingResult => processDateFilters(filters, ALARM_DATE_FILTER_DEFINITIONS);

export const applyFiltersToColumns = (
    columns: Array<ADataGridColumn>,
    filters: Array<ADataGridFilter>
): Array<ADataGridColumn> =>
    columns.map((column) => {
        const nextFilter = filters.find(
            (filter) => filter.name === column.filter?.name && filter.condition === column.filter?.condition
        ) || filters.find((filter) => filter.name === column.filter?.name);

        if (!column.filter) {
            return {...column};
        }

        if (!nextFilter) {
            return {
                ...column,
                filter: {...column.filter},
            };
        }

        return {
            ...column,
            filter: {
                ...column.filter,
                value: nextFilter.value,
                condition: nextFilter.condition,
                sort: nextFilter.sort,
            },
        };
    });

export const applyFiltersToAlarmColumns = applyFiltersToColumns;

const findGridByAriaLabel = (gridAriaLabel: string): Element | null => {
    if (typeof document === "undefined") {
        return null;
    }

    return Array.from(document.querySelectorAll("[aria-label]"))
        .find((element) => element.getAttribute("aria-label") === gridAriaLabel) || null;
};

export const mergePendingDateFilterInputValues = (
    gridAriaLabel: string,
    filters: Array<ADataGridFilter>,
    columns: Array<ADataGridColumn>,
    definitions: DateFilterDefinitions = DATE_FILTER_DEFINITIONS
): Array<ADataGridFilter> => {
    const grid = findGridByAriaLabel(gridAriaLabel);

    if (!grid) {
        return filters.map((filter) => ({...filter}));
    }

    const inputs = Array.from(grid.querySelectorAll<HTMLInputElement>("input[name]"));
    const mergedFilters = filters.map((filter) => ({...filter}));

    columns.forEach((column) => {
        if (!column.filter || !isDateFilter(column.filter, definitions)) {
            return;
        }

        const input = inputs.find((item) => item.name === column.name);

        if (!input) {
            return;
        }

        const nextFilter = {
            ...column.filter,
            value: input.value || "",
        };

        const filterIndex = mergedFilters.findIndex(
            (filter) => filter.name === nextFilter.name && filter.condition === nextFilter.condition
        );

        if (filterIndex >= 0) {
            mergedFilters[filterIndex] = nextFilter;
            return;
        }

        mergedFilters.push(nextFilter);
    });

    return mergedFilters;
};

export const buildDateFilterValidationMessage = (fieldLabels: Array<string>): string => {
    if (fieldLabels.length === 0) {
        return "Informe uma data válida.";
    }

    if (fieldLabels.length === 1) {
        return `Informe uma data válida no campo ${fieldLabels[0]}.`;
    }

    return `Informe datas válidas nos campos: ${fieldLabels.join(", ")}.`;
};

export const buildAlarmDateFilterValidationMessage = buildDateFilterValidationMessage;

export const buildDateFilterSanitizationMessage = (fieldLabels: Array<string>): string => {
    if (fieldLabels.length === 0) {
        return "Use apenas números, /, -, : e espaços nos campos de data.";
    }

    if (fieldLabels.length === 1) {
        return `Use apenas números, /, -, : e espaços no campo ${fieldLabels[0]}.`;
    }

    return `Use apenas números, /, -, : e espaços nos campos: ${fieldLabels.join(", ")}.`;
};

export const buildAlarmDateFilterSanitizationMessage = buildDateFilterSanitizationMessage;
